import { Component, OnInit, NgZone, effect, HostListener } from '@angular/core'
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone'
import { LanguageService } from './services/utils/language.service'
import { NavigationEnd, Router } from '@angular/router'
import { App, URLOpenListenerEvent } from '@capacitor/app'
import { defineCustomElements } from '@ionic/pwa-elements/loader'
import {
  CookiesService,
  CoookiesState,
} from './services/internal/cookies.service'
import { ModalController, AlertController } from '@ionic/angular/standalone'
import { CookiesComponent } from './components/modals/cookies/cookies.component'
import { Capacitor } from '@capacitor/core'
import { AppstateService } from './services/internal/appstate.service'
import { SentryService } from './services/external/sentry.service'
import { URLService } from './services/utils/url.service'
import { filter } from 'rxjs'
import { ScreenOrientation } from '@capacitor/screen-orientation'
import { Stripe } from '@capacitor-community/stripe'
import { NewsStateService } from './services/api-wrappers/news.service'
import { environment } from 'src/environments/environment'
import { PersonnalisationService } from './services/utils/personnalisation.service'
import { KYCWrapperService } from './services/api-wrappers/kyc.service'
import { LaunchService } from './services/internal/launch.service'
import { StripeSetupService } from './services/internal/stripe-setup.service'
import { LiveUpdateService } from './services/liveupdate.service'
import { bypassesLaunch } from './guards/launch-bypass'
@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  imports: [IonApp, IonRouterOutlet],
})
export class AppComponent implements OnInit {
  @HostListener('window:unload', ['$event'])
  unloadHandler(event: any) {
    this.appstateService.saveAppState()
    this.newsStateService.saveMandatoryState()
  }
  @HostListener('window:beforeunload', ['$event'])
  beforeUnloadHandler(event: any) {
    this.appstateService.saveAppState()
    this.newsStateService.saveMandatoryState()
  }

  constructor(
    private language: LanguageService,
    private router: Router,
    private zone: NgZone,
    private cookiesService: CookiesService,
    private modalController: ModalController,
    private alertController: AlertController,
    private appstateService: AppstateService,
    private sentryService: SentryService,
    private urlService: URLService,
    private newsStateService: NewsStateService,
    private personalisationService: PersonnalisationService,
    private kycService: KYCWrapperService,
    private launchService: LaunchService,
    private stripeSetup: StripeSetupService,
    private liveUpdateService: LiveUpdateService
  ) {
    Stripe.initialize({
      publishableKey: environment.stripePublicKey,
    })
    defineCustomElements(window)
    this.initializeApp()
    effect(() => {
      this.onCookiesStateChange(this.cookiesService.cookiesState())
    })
  }

  async ngOnInit(): Promise<void> {
    await this.cookiesService.getPreferences()
    if (Capacitor.isNativePlatform()) {
      await ScreenOrientation.lock({ orientation: 'portrait' })
      if (this.cookiesService.cookiesState().state === 'not_already_set') {
        const preferences = {
          necessary: true,
          analytics: true,
        }
        await this.cookiesService.storePreferences(preferences)
      }
    }

    // CapacitorShake.addListener('shake', () => {
    //   this.sentryService.openFeedbackModal()
    // })

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.urlService.previousUrl.set(this.urlService.currentUrl())
        this.urlService.currentUrl.set(event.url)
      })
  }

  initializeApp() {
    this.language.initializeLanguage()
    this.personalisationService.setTitle()
    this.personalisationService.setColors()
    this.personalisationService.loadLogo()
    this.kycService.setKycDocumentTypes()

    if (Capacitor.isNativePlatform()) {
      // Avant toute autre chose. Le plugin LiveUpdate arme un minuteur de
      // rollback à la création du processus (`readyTimeout`, 10 s) et, s'il
      // expire, repositionne le chemin servi par la WebView sur le bundle par
      // défaut : l'application se recharge à la racine et l'URL en cours -
      // retour Stripe compris - est perdue. Le signal part d'ici, et non de
      // l'écran de lancement, parce que toutes les entrées ne passent pas par
      // cet écran. L'appel ne fait aucun réseau et ne recharge rien : il
      // désarme ce minuteur, et lui seul. La recherche de mise à jour, elle,
      // reste réservée au démarrage à froid via l'écran de lancement.
      this.liveUpdateService.ready().catch((error) => {
        console.error('LiveUpdate.ready() a échoué :', error)
      })

      App.getLaunchUrl().then((result) => {
        if (result?.url) {
          this.zone.run(() => this.handleDeepLinkUrl(result.url))
        }
      })
    }

    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => this.handleDeepLinkUrl(event.url))
    })
  }

  private handleDeepLinkUrl(url: string) {
    const path = this.extractDeepLinkPath(url)
    if (!path) return

    const normalizedPath = path.startsWith('/') ? path.substring(1) : path
    const [routePath, queryString] = normalizedPath.split('?')
    const queryParams: Record<string, string> = { next: `/${routePath}` }

    if (queryString) {
      new URLSearchParams(queryString).forEach((value, key) => {
        queryParams[key] = value
      })
    }

    if (routePath === 'launch') {
      this.router.navigateByUrl(`/${normalizedPath}`)
      return
    }

    // Même règle de contournement que le garde de routage : une page servie
    // sans passer par l'écran de lancement quand on y navigue doit l'être
    // aussi quand on y entre par un lien profond. Le `data` de la route n'est
    // pas résolu ici ; seule l'URL est connue, et c'est elle qui porte les
    // paramètres d'un retour Stripe. Le garde tranchera ensuite sur le
    // marqueur de route, et laissera passer.
    if (
      bypassesLaunch(
        null,
        new URL(normalizedPath, window.location.origin),
        this.stripeSetup.peek() !== null
      )
    ) {
      this.router.navigateByUrl(`/${normalizedPath}`)
      return
    }

    if (this.launchService.getLaunchDataState().state === 'pending') {
      this.router.navigate(['/launch'], { queryParams })
      return
    }

    this.router.navigateByUrl(`/${normalizedPath}`)
  }

  private extractDeepLinkPath(url: string): string {
    if (url.startsWith('com.ecovelo.')) {
      const parts = url.split('://')
      return parts.length > 1 ? parts[1] : ''
    }

    if (url.startsWith('https://')) {
      try {
        const parsed = new URL(url)
        let path = parsed.pathname + parsed.search + parsed.hash
        if (path.startsWith('/')) {
          path = path.substring(1)
        }
        return path
      } catch (error) {
        console.error('Failed to parse URL:', url, error)
      }
    }

    return ''
  }

  private async onCookiesStateChange(state: CoookiesState) {
    switch (state.state) {
      case 'already_set':
        if (state.preferences.analytics) {
          this.sentryService.initSentry()
        }
        break
      case 'not_already_set':
        if (!Capacitor.isNativePlatform()) {
          const modal = await this.modalController.create({
            component: CookiesComponent,
            cssClass: 'ecl-cookies-modal',
            id: 'ecl-cookies-modal',
            backdropDismiss: false,
          })
          await modal.present()
        }
        break
      default:
        break
    }
  }
}
